import { useState, useEffect } from 'react';
import { useNavigate, useSearchParams, Link as RouterLink } from 'react-router-dom';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { Alert, Box, Button, CircularProgress, Divider, Grid, Link, Stack, Typography } from '@mui/material';
import ListAltIcon from '@mui/icons-material/ListAlt';
import CalculateIcon from '@mui/icons-material/Calculate';
import UploadFileIcon from '@mui/icons-material/UploadFile';
import TimelineIcon from '@mui/icons-material/Timeline';
import MarkEmailReadIcon from '@mui/icons-material/MarkEmailRead';
import { authService } from '../../services/authService';
import { getHomeRoute } from '../../config/roles';
import SocialLoginButtons from '../../components/SocialLoginButtons';
import FormProvider from '../../components/form/FormProvider';
import TextField from '../../components/form/TextField';
import PasswordField from '../../components/form/PasswordField';
import { slate } from '../../theme/theme';

const loginSchema = z.object({
  email: z.email('Please enter a valid email address'),
  password: z.string().min(1, 'Password is required'),
});

const FEATURES = [
  { icon: <ListAltIcon />, title: 'Pipeline Management', desc: 'Full visibility into every loan from submission to closing' },
  { icon: <CalculateIcon />, title: 'Pricing & Rate Locking', desc: 'Run pricing scenarios and lock rates instantly' },
  { icon: <UploadFileIcon />, title: 'Document Upload', desc: 'Upload and manage loan documents and conditions in real time' },
  { icon: <TimelineIcon />, title: 'Loan Status Tracking', desc: 'Monitor loan milestones and stage changes as they happen' },
  { icon: <MarkEmailReadIcon />, title: 'Real-Time Email Updates', desc: 'Automated notifications for status changes and condition updates' },
];

const LoginPage = () => {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);

  const methods = useForm({
    resolver: zodResolver(loginSchema),
    defaultValues: { email: '', password: '' },
  });

  const navigateByRole = (user) => {
    navigate(getHomeRoute(user.role));
  };

  // Handle external login callback: ?external_login=success or ?error=...
  useEffect(() => {
    const externalLogin = searchParams.get('external_login');
    const errorParam = searchParams.get('error');

    if (errorParam) {
      setError(decodeURIComponent(errorParam));
      setSearchParams({}, { replace: true });
      return;
    }

    if (externalLogin === 'success') {
      setLoading(true);
      setSearchParams({}, { replace: true });
      authService.exchangeExternalLogin()
        .then((result) => navigateByRole(result.user))
        .catch((err) => {
          setError(err.message || 'External login failed.');
          setLoading(false);
        });
    }
  }, []);

  const handleSubmit = async ({ email, password }) => {
    setError('');
    setLoading(true);

    try {
      const result = await authService.login(email, password);
      navigateByRole(result.user);
    } catch (err) {
      setError(err.message || 'Login failed. Please check your credentials.');
    } finally {
      setLoading(false);
    }
  };

  const handleForgotPassword = async (e) => {
    e.preventDefault();
    const email = methods.getValues('email');
    const emailValid = loginSchema.shape.email.safeParse(email);
    const resetUrl = emailValid.success
      ? `/auth/reset-password?email=${encodeURIComponent(email)}`
      : '/auth/reset-password';
    navigate(resetUrl);
  };

  const handleGoogleLogin = () => {
    authService.loginWithGoogle();
  };

  const handleMicrosoftLogin = () => {
    authService.loginWithMicrosoft();
  };

  return (
    <Grid container sx={{ minHeight: '100vh' }}>
      {/* Left — brand hero (desktop only) */}
      <Grid
        size={{ xs: 12, lg: 5 }}
        sx={{
          display: { xs: 'none', lg: 'flex' },
          flexDirection: 'column',
          position: 'relative',
          overflow: 'hidden',
          p: 6,
          color: '#fff',
          background: `linear-gradient(160deg, ${slate[900]} 0%, ${slate[800]} 55%, ${slate[700]} 100%)`,
        }}
      >
        {/* Decorative glow accents */}
        <Box sx={{ position: 'absolute', top: -120, right: -120, width: 380, height: 380, borderRadius: '50%', background: 'radial-gradient(circle, rgba(225,4,4,0.18) 0%, transparent 65%)' }} />
        <Box sx={{ position: 'absolute', bottom: -160, left: -100, width: 420, height: 420, borderRadius: '50%', background: 'radial-gradient(circle, rgba(143,163,173,0.14) 0%, transparent 65%)' }} />

        <Box sx={{ position: 'relative', zIndex: 1, display: 'flex', flexDirection: 'column', height: '100%' }}>
          <Box sx={{ display: 'inline-flex', bgcolor: 'rgba(255,255,255,0.92)', borderRadius: 2, px: 2, py: 1.25, alignSelf: 'flex-start', mb: 5 }}>
            <Box component="img" src="/assets/pp-logo.png" alt="PRO Portal" sx={{ height: 34 }} />
          </Box>

          <Typography variant="h3" fontWeight={700} sx={{ letterSpacing: '-0.02em', mb: 1.5, maxWidth: 420 }}>
            Everything you need to manage your pipeline
          </Typography>
          <Typography variant="body1" sx={{ color: 'rgba(255,255,255,0.72)', mb: 4, maxWidth: 400, lineHeight: 1.7 }}>
            One place to originate, price, lock, and close loans with United Fidelity Funding.
          </Typography>

          <Stack spacing={2.25}>
            {FEATURES.map(({ icon, title, desc }) => (
              <Stack key={title} direction="row" spacing={1.75} alignItems="flex-start">
                <Box
                  sx={{
                    width: 38,
                    height: 38,
                    borderRadius: 2,
                    bgcolor: 'rgba(255,255,255,0.08)',
                    border: '1px solid rgba(255,255,255,0.12)',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    flexShrink: 0,
                    color: '#ff6b6b',
                    '& .MuiSvgIcon-root': { fontSize: 20 },
                  }}
                >
                  {icon}
                </Box>
                <Box>
                  <Typography variant="body2" fontWeight={600} sx={{ color: '#fff' }}>{title}</Typography>
                  <Typography variant="caption" sx={{ color: 'rgba(255,255,255,0.6)' }}>{desc}</Typography>
                </Box>
              </Stack>
            ))}
          </Stack>

          <Box sx={{ mt: 'auto', pt: 4, borderTop: '1px solid rgba(255,255,255,0.12)' }}>
            <Typography variant="caption" sx={{ color: 'rgba(255,255,255,0.5)', fontStyle: 'italic', display: 'block', mb: 1.5 }}>
              Powering Real Originators with the tools they need to grow.
            </Typography>
            <Typography variant="caption" sx={{ color: 'rgba(255,255,255,0.45)', textTransform: 'uppercase', letterSpacing: 1, display: 'block', mb: 0.75 }}>
              Powered by
            </Typography>
            <Link href="https://uff.pro" target="_blank" rel="noopener noreferrer">
              <Box
                component="img"
                src="/assets/UFF_Logo_Main_2026.png"
                alt="United Fidelity Funding"
                sx={{ height: 30, filter: 'brightness(0) invert(1)', opacity: 0.55, '&:hover': { opacity: 0.85 }, transition: 'opacity 0.2s' }}
              />
            </Link>
          </Box>
        </Box>
      </Grid>

      {/* Right — login form */}
      <Grid
        size={{ xs: 12, lg: 7 }}
        sx={{
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          bgcolor: 'background.default',
          p: { xs: 3, sm: 6 },
        }}
      >
        <Box sx={{ width: '100%', maxWidth: 400 }}>
          {/* Mobile logo */}
          <Box sx={{ display: { xs: 'flex', lg: 'none' }, justifyContent: 'center', mb: 4 }}>
            <Box component="img" src="/assets/pp-logo.png" alt="PRO Portal" sx={{ height: 44 }} />
          </Box>

          <Box sx={{ mb: 4 }}>
            <Typography variant="h4" fontWeight={700} sx={{ letterSpacing: '-0.02em' }}>Welcome back</Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
              Sign in to your broker account
            </Typography>
          </Box>

          {error && <Alert severity="error" sx={{ mb: 2.5 }}>{error}</Alert>}

          <FormProvider methods={methods} onSubmit={handleSubmit} autoComplete="off">
            <TextField
              name="email"
              label="Email"
              type="email"
              placeholder="you@company.com"
              disabled={loading}
              size="small"
              autoComplete="off"
              sx={{ mb: 2 }}
            />

            <PasswordField
              name="password"
              label="Password"
              placeholder="Enter your password"
              disabled={loading}
              size="small"
              autoComplete="off"
            />

            <Box sx={{ display: 'flex', justifyContent: 'end', alignItems: 'center', mb: 1.5 }}>
              <Link
                component="button"
                type="button"
                variant="caption"
                color="secondary"
                underline="none"
                tabIndex={-1}
                onClick={handleForgotPassword}
                sx={{ fontWeight: 600 }}
              >
                Forgot password?
              </Link>
            </Box>

            <Button
              type="submit"
              variant="contained"
              color="primary"
              fullWidth
              disabled={loading}
              sx={{ py: 1.2, fontWeight: 600, fontSize: '0.875rem' }}
            >
              {loading ? (
                <><CircularProgress size={18} color="inherit" sx={{ mr: 1 }} /> Signing in...</>
              ) : (
                'Sign in'
              )}
            </Button>

            <Divider sx={{ my: 2.5 }}>
              <Typography variant="caption" color="text.disabled">OR</Typography>
            </Divider>

            <SocialLoginButtons
              onGoogleLogin={handleGoogleLogin}
              onMicrosoftLogin={handleMicrosoftLogin}
              loading={loading}
              googleLabel="Continue with Google"
              microsoftLabel="Continue with Microsoft"
            />
          </FormProvider>

          <Typography variant="body2" color="text.secondary" align="center" sx={{ mt: 3.5 }}>
            Are you a broker?{' '}
            <Link component={RouterLink} to="/signup" color="secondary" fontWeight={600} underline="none">
              Apply to become a partner
            </Link>
          </Typography>

          {/* Mobile UFF footer */}
          <Box sx={{ display: { xs: 'block', lg: 'none' }, textAlign: 'center', mt: 3 }}>
            <Typography variant="caption" color="text.disabled" sx={{ textTransform: 'uppercase', letterSpacing: 1, display: 'block' }}>
              Powered by
            </Typography>
            <Link href="https://uff.pro" target="_blank" rel="noopener noreferrer">
              <Box component="img" src="/assets/UFF_Logo_Main_2026.png" alt="United Fidelity Funding" sx={{ height: 26, opacity: 0.4, mt: 0.5 }} />
            </Link>
          </Box>
        </Box>
      </Grid>
    </Grid>
  );
};

export default LoginPage;
